import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { GovItem, GovKind } from "../../../../utils/icGovernance";
import { TICKET_VIEW_URL } from "../../Support/supportView";
import { useViewport } from "../../../../utils/useViewport";
import { CARD_BORDER, PRIMARY, itemUrl } from "../governanceStatus";
import { INK, MUTED } from "./cells";
import { ColumnsMenu, ExportButton, SearchBox, SortSelect, StatusFilter } from "./RegisterFilters";
import { RegisterCard, WaitingNotice, WaitingNoticeBox } from "./RegisterParts";
import { RegisterState } from "./useRegisterItems";

/**
 * One governance register: toolbar (count, create button, Columns, Export),
 * filter row (search, status, sort), the amber "waiting for you" notice, the
 * workbook columns in a sideways-scrolling table (tablet and up) or a card
 * list (phones), and "Load more". The five registers only declare columns.
 */

export interface RegisterColumn<T extends GovItem> {
  /** Stable key (used for the Columns menu and its saved choice). */
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Starts hidden behind the Columns menu (long text). */
  hideByDefault?: boolean;
  minWidth?: number;
  /** Long text: wraps inside a bounded width instead of one line. */
  wrap?: boolean;
  align?: "left" | "right";
}

export type { WaitingNotice };

export type RegisterTableProps<T extends GovItem> = RegisterState<T> & {
  kind: GovKind;
  columns: RegisterColumn<T>[];
  noun: { one: string; many: string };
  /** One short line next to the count, e.g. "IDEACORE maintains; OT signs off". */
  caption?: ReactNode;
  /** Create button(s), right of the count. */
  actions?: ReactNode;
  waiting?: WaitingNotice<T>;
  /** Shown above the table (the UAT register's release banner). */
  banner?: ReactNode;
  /** A row of preset filters above the search/status/sort row (the Issue register). */
  quickFilters?: ReactNode;
  /** `rows` were narrowed client-side (the Issue register's mine / reply filters). */
  clientFiltered?: boolean;
  /** Statuses offered by the filter (default: the kind's own). */
  statuses?: string[];
  /** Spreadsheet exports, one button each (default: one for `kind`). IC_Ticket exports one kind per file. */
  exports?: { kind: GovKind; label: string }[];
};

const storageKey = (kind: GovKind) => `governance.columns.${kind}`;

const defaultHidden = <T extends GovItem>(columns: RegisterColumn<T>[]) =>
  new Set(columns.filter((c) => c.hideByDefault).map((c) => c.key));

const readHidden = <T extends GovItem>(kind: GovKind, columns: RegisterColumn<T>[]): Set<string> => {
  try {
    const raw = window.localStorage.getItem(storageKey(kind));
    if (raw) {
      const keys = JSON.parse(raw);
      if (Array.isArray(keys)) return new Set(keys.filter((k) => typeof k === "string"));
    }
  } catch {
    // storage blocked or corrupt — fall back to the defaults
  }
  return defaultHidden(columns);
};

const writeHidden = (kind: GovKind, hidden: Set<string> | null) => {
  try {
    if (hidden) window.localStorage.setItem(storageKey(kind), JSON.stringify(Array.from(hidden)));
    else window.localStorage.removeItem(storageKey(kind));
  } catch {
    // a convenience only
  }
};

// ---------------------------------------------------------------------------
// The register
// ---------------------------------------------------------------------------

const RegisterTable = <T extends GovItem>(props: RegisterTableProps<T>) => {
  const {
    kind,
    columns,
    noun,
    caption,
    actions,
    waiting,
    banner,
    quickFilters,
    clientFiltered,
    statuses,
    exports,
    rows,
    loading,
    loadingMore,
    error,
    nextCursor,
    onLoadMore,
    query,
    onQueryChange,
  } = props;
  const navigate = useNavigate();
  const { isPhone } = useViewport();
  const [hidden, setHidden] = useState<Set<string>>(() => readHidden(kind, columns));
  const [exportError, setExportError] = useState<string | null>(null);

  const visibleColumns = useMemo(() => columns.filter((c) => !hidden.has(c.key)), [columns, hidden]);

  const toggleColumn = useCallback(
    (key: string) => {
      setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        writeHidden(kind, next);
        return next;
      });
    },
    [kind],
  );
  const resetColumns = useCallback(() => {
    writeHidden(kind, null);
    setHidden(defaultHidden(columns));
  }, [kind, columns]);

  const onSearch = useCallback((q: string) => onQueryChange({ q }), [onQueryChange]);

  const filtered = query.status.length > 0 || query.q !== "" || !!clientFiltered;
  const countText = `${rows.length}${nextCursor ? "+" : ""} ${rows.length === 1 && !nextCursor ? noun.one : noun.many}`;
  const emptyText =
    clientFiltered && nextCursor
      ? `No ${noun.many} match in the rows loaded so far — load more to keep looking.`
      : filtered
        ? `No ${noun.many} match these filters.`
        : `No ${noun.many} yet.`;

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        useFlexGap
        sx={{ flexWrap: "wrap", mb: 1.5 }}
      >
        <Typography sx={{ fontWeight: 700, color: INK }}>{loading ? noun.many : countText}</Typography>
        {caption ? (
          <Typography variant="body2" sx={{ color: MUTED }}>
            {caption}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1} useFlexGap sx={{ ml: "auto", flexWrap: "wrap" }}>
          {actions}
          {!isPhone ? (
            <ColumnsMenu columns={columns} hidden={hidden} onToggle={toggleColumn} onReset={resetColumns} />
          ) : null}
          {(exports ?? [{ kind, label: "Export .xlsx" }]).map((e) => (
            <ExportButton key={e.kind} kind={e.kind} label={e.label} onError={setExportError} />
          ))}
        </Stack>
      </Stack>

      {exportError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      ) : null}

      {quickFilters}

      {/* Filters */}
      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: "wrap", mb: 2 }}>
        <SearchBox value={query.q} onChange={onSearch} />
        <StatusFilter
          kind={kind}
          statuses={statuses}
          value={query.status}
          onChange={(status) => onQueryChange({ status })}
          compact={isPhone}
        />
        <SortSelect value={query.sort} onChange={(sort) => onQueryChange({ sort })} />
      </Stack>

      {banner}

      {waiting && !loading ? <WaitingNoticeBox rows={rows} waiting={waiting} partial={!!nextCursor} /> : null}

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button component={Link} to={TICKET_VIEW_URL} color="inherit" size="small" sx={{ whiteSpace: "nowrap" }}>
              Open support tickets instead
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", textAlign: "center", py: 6 }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Paper>
      ) : rows.length === 0 ? (
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px" }}>
          <Typography sx={{ textAlign: "center", color: MUTED, py: 5, px: 2 }}>
            {error ? `No ${noun.many} to show.` : emptyText}
          </Typography>
        </Paper>
      ) : isPhone ? (
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", overflow: "hidden" }}>
          {rows.map((r) => (
            <RegisterCard key={r.id} row={r} />
          ))}
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: CARD_BORDER,
            borderRadius: "12px",
            // width 0 + min-width 100%: the wide table never widens the page;
            // it scrolls sideways (and down, so the header can stick) in here.
            width: 0,
            minWidth: "100%",
            overflow: "auto",
            maxHeight: "max(360px, calc(100vh - 200px))",
          }}
        >
          <Table size="small" stickyHeader aria-label={`${noun.many} register`}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: "#3f4753",
                      whiteSpace: "nowrap",
                      backgroundColor: "#f8f9fb",
                    }}
                  >
                    {c.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(itemUrl(r))}>
                  {visibleColumns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sx={{
                        verticalAlign: "top",
                        color: INK,
                        whiteSpace: c.wrap ? "normal" : "nowrap",
                        minWidth: c.minWidth ?? (c.wrap ? 240 : undefined),
                        maxWidth: c.wrap ? 360 : undefined,
                        fontVariantNumeric: c.align === "right" ? "tabular-nums" : undefined,
                      }}
                    >
                      {c.render(r)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Client-side filters can empty the loaded rows while more pages wait. */}
      {!loading && (rows.length > 0 || nextCursor) ? (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent={isPhone ? "center" : "flex-start"}
          useFlexGap
          sx={{ flexWrap: "wrap", mt: 1.5 }}
        >
          {rows.length > 0 ? (
            <Typography variant="body2" sx={{ color: MUTED }}>
              Showing {rows.length} {rows.length === 1 ? noun.one : noun.many}
              {!isPhone ? ` · scroll sideways for all ${visibleColumns.length} columns` : ""}
            </Typography>
          ) : null}
          {nextCursor ? (
            <Button
              variant="outlined"
              onClick={onLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={16} /> : null}
              sx={{ color: PRIMARY, borderColor: PRIMARY }}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};

export default RegisterTable;
